import React, {useState} from "react";
import PropTypes from "prop-types";
import {useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {
    contentUtils,
    HTMLContent,
    ErrorMessageAction,
    LoadingAction,
    CreateQuestionPracticeSkillAction,
    DeleteQuestionPracticeSkillAction,
    UpdateQuestionPracticeSkillAction
} from "mindsets-js-sdk";

import CorrectAnswer from "./CorrectAnswer";
import SkillSelector from "./SkillSelector";
import {getDummyQuestionPracticeSkill, isNumeric} from "./utils";
import {SetQuestionPracticeSkillsUpdatedAction} from "../../actions/question_actions";
import {SetSelectedAssignmentIndexAction} from "../../actions/practice_actions";
import GradingInstructions from "./GradingInstructions";
import DependsOn from "./DependsOn";
import GradingMode from "./GradingMode";

const STANDARD_PRACTICE_MAPPING = {
    ccmps: ['ccss', 'coas', 'njsls', 'nmrs', 'wasls', 'vic', 'cbse', 'gse', 'nysngls'],
    ngssps: ['ccss', 'coas', 'njsls', 'nmrs', 'wasls', 'vic', 'cbse', 'gse', 'nysngls', 'okas'],
    teksmps: ['teks'],
    bestmtrs: ['best'],
    camps: ['caccss']
};

function UpdateRubricForm({question, loadQuestionPracticeSkills, updated}) {
    const params = useParams();
    const dispatch = useDispatch();
    const practices = useSelector(state => state.practices);
    const standard_topics = useSelector(state => state.standard_topics);
    const question_practice_skills = useSelector(state => state.question_practice_skills);
    const selected_practice_id = useSelector(state => state.selected_practice_id);
    const selected_assignment_index = useSelector(state => state.selected_assignment_index);
    const question_practice_skills_updated = useSelector(state => state.question_practice_skills_updated);
    const [submitting, setSubmitting] = useState(false);
    const question_practice_skills_selected = question_practice_skills_updated.filter(skill => skill.question_code === question.code && skill.practice_id === selected_practice_id);
    const question_practice_skill_updated = question_practice_skills_selected[selected_assignment_index];
    const practice = practices.find(practice => practice.id === selected_practice_id);
    const topics = standard_topics.filter(topic => practice && practice.code && Array.isArray(STANDARD_PRACTICE_MAPPING[practice.code]) && STANDARD_PRACTICE_MAPPING[practice.code].includes(topic.curriculum_code)).map(topic => topic.topic);

    function renderDeleteButton() {
        if (question_practice_skill_updated && question_practice_skill_updated.id > 0) {
            return (
                <button
                    className="button red"
                    onClick={event => {
                        event.preventDefault();
                        const question_practice_skills_updated_x = JSON.parse(JSON.stringify(question_practice_skills_updated));
                        dispatch(DeleteQuestionPracticeSkillAction(question_practice_skill_updated.id)).then(() => {
                            question_practice_skills_updated_x[selected_assignment_index] = JSON.parse(JSON.stringify(getDummyQuestionPracticeSkill(question.code, selected_practice_id)));
                            dispatch(SetQuestionPracticeSkillsUpdatedAction(question_practice_skills_updated_x));
                        }).then(loadQuestionPracticeSkills);
                    }}
                >Delete data for this skill assignment
                </button>
            );
        }

        return null;
    }

    function renderResetButton() {
        if (updated) {
            return (
                <button
                    className="button"
                    onClick={event => {
                        event.preventDefault();
                        const question_practice_skills_updated_x = JSON.parse(JSON.stringify(question_practice_skills_updated));

                        if (question_practice_skill_updated && question_practice_skill_updated.id > 0) {
                            question_practice_skills_updated_x[selected_assignment_index] = JSON.parse(JSON.stringify(question_practice_skills[selected_assignment_index]));
                        } else {
                            question_practice_skills_updated_x.splice(selected_assignment_index, 1);
                        }
                        dispatch(SetSelectedAssignmentIndexAction(null));
                        dispatch(SetQuestionPracticeSkillsUpdatedAction(question_practice_skills_updated_x));
                    }}
                >Remove all changes
                </button>
            );
        }

        return null;
    }

    function renderSaveButton() {
        if (updated) {
            return (
                <button
                    className="button"
                    onClick={event => {
                        event.preventDefault();
                        if (validate()) {
                            if (question_practice_skill_updated.id > 0) {
                                // TODO A state mutation was detected between dispatches, in the path 'question_practice_skills_updated.28.questions.0'.
                                Promise.resolve()
                                    .then(() => setSubmitting(true))
                                    .then(() => dispatch(UpdateQuestionPracticeSkillAction(question_practice_skill_updated.id, {
                                        practice_skill_id: question_practice_skill_updated.practice_skill_id,
                                        dok: question_practice_skill_updated.dok ? question_practice_skill_updated.dok : null,
                                        correct_answer: question_practice_skill_updated.correct_answer,
                                        max_points: question_practice_skill_updated.max_points,
                                        mode: question_practice_skill_updated.mode,
                                        grading_instructions: question_practice_skill_updated.grading_instructions,
                                        domain: question_practice_skill_updated.domain,
                                        question_codes: question_practice_skill_updated.questions ? question_practice_skill_updated.questions : null
                                    })))
                                    .then(loadQuestionPracticeSkills)
                                    .then(() => setSubmitting(false))
                                    .catch(error => {
                                        dispatch(LoadingAction(false));
                                        dispatch(ErrorMessageAction(error));
                                        console.error(error);
                                    });
                            } else {
                                Promise.resolve()
                                    .then(() => setSubmitting(true))
                                    .then(() => dispatch(CreateQuestionPracticeSkillAction({
                                        question_code: question.code,
                                        challenge_code: params.challenge_code,
                                        practice_skill_id: question_practice_skill_updated.practice_skill_id,
                                        dok: question_practice_skill_updated.dok ? question_practice_skill_updated.dok : null,
                                        mode: question_practice_skill_updated.mode,
                                        correct_answer: question_practice_skill_updated.correct_answer,
                                        grading_instructions: question_practice_skill_updated.grading_instructions,
                                        max_points: question_practice_skill_updated.max_points,
                                        domain: question_practice_skill_updated.domain,
                                        question_codes: question_practice_skill_updated.questions ? question_practice_skill_updated.questions : null
                                    })))
                                    .then(loadQuestionPracticeSkills)
                                    .then(() => setSubmitting(false))
                                    .catch(error => {
                                        dispatch(LoadingAction(false));
                                        dispatch(ErrorMessageAction(error));
                                        console.error(error);
                                    });
                            }
                        }
                    }}
                    disabled={submitting}
                >Submit
                </button>
            );
        }

        return null;
    }

    function validate() {
        let errors = 0;

        if (!question_practice_skill_updated.practice_skill_id) {
            dispatch(ErrorMessageAction('Error! Please select math practice category for question #' + question.number + '.'));
            errors++;
        }
        if (!question_practice_skill_updated.mode) {
            dispatch(ErrorMessageAction('Error! Please define Grading Mode for question #' + question.number + '.'));
            errors++;
        }
        if (!question_practice_skill_updated.max_points) {
            dispatch(ErrorMessageAction('Error! Please define max points for question #' + question.number + '.'));
            errors++;
        }
        if (question_practice_skill_updated.mode === 'ai') {
            if (!Array.isArray(question_practice_skill_updated.grading_instructions)) {
                dispatch(ErrorMessageAction('Error! Please provide Grading Instructions for question #' + question.number + '.'));
                errors++;
            } else if (question_practice_skill_updated.grading_instructions.length - 1 !== question_practice_skill_updated.max_points) {
                dispatch(ErrorMessageAction('Error! Some Grading Instructions are missing for question #' + question.number + '.'));
                errors++;
            } else {
                question_practice_skill_updated.grading_instructions.map((instr, i) => {
                    if (!instr) {
                        dispatch(ErrorMessageAction('Error! Grading Instruction for score "' + i + '" is missing for question #' + question.number + '.'));
                        errors++;
                    }
                })
            }
        }
        if (question_practice_skill_updated.mode === 'correct_answer') {
            if (!question_practice_skill_updated.correct_answer) {
                dispatch(ErrorMessageAction('Error! Please set correct answer for question #' + question.number + '.'));
                errors++;
            }
            if ((question.answer_type === 'answer.slider' || question.answer_type === 'answer.range' || question.answer_type === 'answer.number_line') && null !== question_practice_skill_updated.correct_answer && typeof question_practice_skill_updated.correct_answer === 'object') {
                if (!question_practice_skill_updated.correct_answer.hasOwnProperty('min') || null === question_practice_skill_updated.correct_answer.min) {
                    dispatch(ErrorMessageAction('Error! Please set Min correct answer for question #' + question.number + '.'));
                    errors++;
                } else if (!question_practice_skill_updated.correct_answer.hasOwnProperty('max') || null === question_practice_skill_updated.correct_answer.max) {
                    dispatch(ErrorMessageAction('Error! Please set Max correct answer for question #' + question.number + '.'));
                    errors++;
                } else if (!isNumeric(question_practice_skill_updated.correct_answer.min)) {
                    dispatch(ErrorMessageAction('Error! Min correct answer for question #' + question.number + ' must be numeric.'));
                    errors++;
                } else if (!isNumeric(question_practice_skill_updated.correct_answer.max)) {
                    dispatch(ErrorMessageAction('Error! Max correct answer for question #' + question.number + ' must be numeric.'));
                    errors++;
                } else if (question_practice_skill_updated.correct_answer.max <= question_practice_skill_updated.correct_answer.min) {
                    dispatch(ErrorMessageAction('Error! Max correct answer for question #' + question.number + ' must be greater than Min.'));
                    errors++;
                }
            }
        }

        return errors === 0;
    }

    function renderDuplicate() {
        if (selected_practice_id && null !== selected_assignment_index) {
            return (
                <div
                    className='bordered clickable'
                    onClick={() => {
                        if (updated) {
                            dispatch(ErrorMessageAction('You have unsaved data. Save it before moving to the next Practice.'));
                        } else {
                            const question_practice_skill_updated_copy = {
                                id: -1,
                                practice_skill_id: '',
                                dok: question_practice_skill_updated.dok,
                                question_code: question_practice_skill_updated.question_code,
                                correct_answer: question_practice_skill_updated.correct_answer,
                                max_points: question_practice_skill_updated.max_points,
                                questions: null,
                                domain: question_practice_skill_updated.domain,
                                mode: question_practice_skill_updated.mode,
                                grading_instructions: question_practice_skill_updated.grading_instructions,
                                practice_id: question_practice_skill_updated.practice_id,
                                node: null
                            };
                            const question_practice_skills_updated_copy = JSON.parse(JSON.stringify(question_practice_skills_updated));
                            question_practice_skills_updated_copy.push(question_practice_skill_updated_copy);
                            Promise.resolve()
                                .then(() => dispatch(SetQuestionPracticeSkillsUpdatedAction(question_practice_skills_updated_copy)))
                                .then(() => dispatch(SetSelectedAssignmentIndexAction(question_practice_skills_selected.length)));
                        }
                    }}
                >Duplicate selected
                </div>
            );
        }

        return null;
    }

    function renderAssignmentTabs() {
        if (!selected_practice_id) {
            return null;
        }

        return (
            <div className='d-flex flex-gap-5 my-2'>
                {question_practice_skills_selected.map((item, index) => {
                    const practice = practices.find(p => p.id === item.practice_id);
                    let practice_skill = null;
                    if (practice && practice.practice_skills) {
                        practice_skill = practice.practice_skills.find(ps => ps.id === item.practice_skill_id);
                    }
                    return (
                        <div
                            key={index}
                            className={'bordered clickable hoverable' + (index === selected_assignment_index ? ' selected' : '')}
                            onClick={() => {
                                if (updated) {
                                    dispatch(ErrorMessageAction('You have unsaved data. Save it before moving to the next Practice.'));
                                } else {
                                    dispatch(SetSelectedAssignmentIndexAction(index));
                                }
                            }}
                        >{practice_skill ? practice_skill.code : 'Unassigned'}</div>
                    );
                })}
                <div
                    className='bordered clickable'
                    onClick={() => {
                        if (updated) {
                            dispatch(ErrorMessageAction('You have unsaved data. Save it before moving to the next Practice.'));
                        } else {
                            const question_practice_skills_updated_copy = JSON.parse(JSON.stringify(question_practice_skills_updated));
                            question_practice_skills_updated_copy.push(JSON.parse(JSON.stringify(getDummyQuestionPracticeSkill(question.code, selected_practice_id))));
                            Promise.resolve()
                                .then(() => dispatch(SetQuestionPracticeSkillsUpdatedAction(question_practice_skills_updated_copy)))
                                .then(() => dispatch(SetSelectedAssignmentIndexAction(question_practice_skills_selected.length)));
                        }
                    }}
                >Add Skill Assignment
                </div>
                {renderDuplicate()}
            </div>
        );
    }

    function renderForm() {
        if (!question_practice_skill_updated) {
            return null;
        }
        if (!selected_practice_id) {
            return null;
        }
        if (isNaN(parseInt(selected_assignment_index)) || selected_assignment_index < 0 || selected_assignment_index > question_practice_skills_selected.length - 1) {
            return null;
        }

        return (
            <form
                onSubmit={event => event.preventDefault()}
                id={"question_practice_skill-form-" + question.code}
                className='question_practice_skill-form'
            >
                <div className='bordered mb-4'>
                    <h3 className='d-flex justify-content-between'>
                        <HTMLContent
                            html_str={contentUtils.processStringContent(question.title, {convert_spelling: false})}/>
                        <i className={'fas ' + (updated ? 'fa-radiation-alt text-danger' : 'fa-check text-success')}/>
                    </h3>
                    <div className="row my-4">
                        <div className="col-sm-6">
                            <label className='form-label'>Skill</label>
                            <SkillSelector question_practice_skill={question_practice_skill_updated}
                                           key={question_practice_skill_updated.id}/>
                        </div>
                        <div className="col-sm-6">
                            <div className='row'>
                                <div className="col-sm-6">
                                    <label className='form-label'>DOK(Optional)</label>
                                    <div>
                                        <select
                                            className='little'
                                            value={question_practice_skill_updated && question_practice_skill_updated.dok ? question_practice_skill_updated.dok : ''}
                                            onChange={event => {
                                                const val = event.target.value;
                                                const question_practice_skills_updated_x = JSON.parse(JSON.stringify(question_practice_skills_updated));
                                                question_practice_skills_updated_x[selected_assignment_index].dok = !isNaN(parseInt(val)) ? parseInt(val) : '';
                                                dispatch(SetQuestionPracticeSkillsUpdatedAction(question_practice_skills_updated_x));
                                            }}
                                        >
                                            <option value=''>None</option>
                                            <option value={1}>1 - Recall and Reproduction</option>
                                            <option value={2}>2 - Application</option>
                                            <option value={3}>3 - Strategic Thinking and Reasoning</option>
                                            <option value={4}>4 - Extended Thinking</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <label className='form-label'>Domain(Optional)</label>
                                    <select
                                        className='little'
                                        value={question_practice_skill_updated && question_practice_skill_updated.domain ? question_practice_skill_updated.domain : ''}
                                        onChange={event => {
                                            const val = event.target.value;
                                            const question_practice_skills_updated_x = JSON.parse(JSON.stringify(question_practice_skills_updated));
                                            question_practice_skills_updated_x[selected_assignment_index].domain = val;
                                            dispatch(SetQuestionPracticeSkillsUpdatedAction(question_practice_skills_updated_x));
                                        }}
                                    >
                                        <option value=''>None</option>
                                        {topics.map(topic => {
                                            return (
                                                <option key={topic} value={topic}>{topic}</option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row my-4">
                        <GradingMode question={question}/>
                        <div className='col-sm-4'>
                            <label>Max points</label>
                            <select
                                value={question_practice_skill_updated.max_points ? question_practice_skill_updated.max_points : 0}
                                onChange={event => {
                                    event.preventDefault();
                                    const max_points = parseInt(event.target.value);
                                    const question_practice_skills_updated_x = JSON.parse(JSON.stringify(question_practice_skills_updated));
                                    question_practice_skills_updated_x[selected_assignment_index].max_points = max_points;
                                    if (question_practice_skills_updated_x[selected_assignment_index].mode === 'ai') {
                                        if (Array.isArray(question_practice_skills_updated_x[selected_assignment_index].grading_instructions)) {
                                            const grading_instructions = question_practice_skills_updated_x[selected_assignment_index].grading_instructions;
                                            if (grading_instructions.length - 1 < max_points) {
                                                let point = grading_instructions.length;
                                                do {
                                                    grading_instructions.push('');
                                                    point++;
                                                } while (grading_instructions.length - 1 < max_points);
                                            } else if (grading_instructions.length - 1 > max_points) {
                                                grading_instructions.splice(-((grading_instructions.length - 1) - max_points));
                                            }
                                            question_practice_skills_updated_x[selected_assignment_index].grading_instructions = grading_instructions;
                                        } else {
                                            question_practice_skills_updated_x[selected_assignment_index].grading_instructions = Array(max_points).fill('');
                                        }
                                    }
                                    dispatch(SetQuestionPracticeSkillsUpdatedAction(question_practice_skills_updated_x));
                                }}
                                disabled={question.answer_type === 'answer.range' && question_practice_skill_updated.mode === 'correct_answer' && question_practice_skill_updated.max_points === 2}
                            >
                                <option value={0} disabled={true}>Select max points</option>
                                <option value={1}>1</option>
                                <option value={2}>2</option>
                                <option value={3}>3</option>
                                <option value={4}>4</option>
                                <option value={5}>5</option>
                            </select>
                        </div>
                        <DependsOn question={question}/>
                    </div>
                    <CorrectAnswer question={question}/>

                    <GradingInstructions/>
                </div>
                <div className='d-flex justify-content-between'>
                    {renderSaveButton()}
                    {renderResetButton()}
                    {renderDeleteButton()}
                </div>
            </form>
        );
    }

    return (
        <div>
            {renderAssignmentTabs()}
            {renderForm()}
        </div>
    );
}

UpdateRubricForm.propTypes = {
    question: PropTypes.shape({
        code: PropTypes.string.isRequired,
        answer_type: PropTypes.string.isRequired
    }).isRequired,
    loadQuestionPracticeSkills: PropTypes.func.isRequired,
    updated: PropTypes.bool.isRequired
}

export default UpdateRubricForm;
